import GenericTrackListView from '../components/common/GenericTrackListView';

import { getQueueOrigin } from '../lib/queue';
import { loadPlaylist } from '../actions/playlist';
import * as analyticsActions from '../actions/analytics';
import { showAddToPlaylistModal } from '../actions/ui';
import * as playerActions from '../actions/player';

import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import dataComponent from '../hoc/dataComponent';
import collectibleEntity from '../hoc/collectibleEntity';
import shareableEntity from '../hoc/shareableEntity';
import queueOriginComponent from '../hoc/queueOriginComponent';
import trackedViewComponent from '../hoc/trackedViewComponent';

import { selectCollectionIds } from '../selectors/collection';
import { selectUserIsPatron } from '../selectors/user';
import { buildPlaylistSchema } from '../lib/googleSchema';

import {
  selectPlaylist,
  selectPlaylistIsPlaying,
  selectPlaylistIsQueued,
  selectPlaylistImageAnnotation,
  getPlaylistQueueOrigin,
  selectPlaylistIdFromSlug,
} from '../selectors/playlist';

import { ENTITY_TYPE_PLAYLIST, META_TITLE_PREFIX } from '../constants';

import { defineMessages } from 'react-intl';
import playlistableEntity from '../hoc/playlistableEntity';
import * as clientActions from '../actions/client';
import { IMGIX_STYLE_4, imgixImageUrl, PLAYABLE_ITEM_FULL_DIMS } from '../lib/imgix';

function fetchData(store, { slug }) {
  return [store.dispatch(loadPlaylist(slug))];
}

export const messages = defineMessages({
  playAllButtonPausedText: {
    id: 'playlist.play-all.paused.title',
    defaultMessage: 'Play playlist',
  },
  playAllButtonPlayingText: {
    id: 'playlist.play-all.playing.title',
    defaultMessage: 'Pause playlist',
  },
  playlistMetaDescription: {
    id: 'playlist.meta.description',
    defaultMessage:
      'Want to listen to our playlist {title}? Stream your favorite classical playlists now on IDAGIO',
  },
  playlistMetaKeywords: {
    id: 'playlist.meta.keywords',
    defaultMessage: '{title}, stream playists on idagio, classical playlists ',
  },
});

function mapStateToProps(state, ownProps) {
  const slug = ownProps.params.slug;
  return {
    playlist: selectPlaylist(state, slug),
    userIsPatron: selectUserIsPatron(state),
    collectionIds: selectCollectionIds(state),
    imageAnnotation: selectPlaylistImageAnnotation(state, slug),
    pathname: ownProps.location.pathname,
    messages,
  };
}

function getCollectibleEntityDescription(props) {
  return {
    id: props.playlist.id,
    trackingSource: 'Playlist',
  };
}

function mapQueueOriginStateToProps(state, ownProps) {
  const { slug, tracks, trackIds } = ownProps.playlist;
  return {
    originId: slug,

    tracks,
    trackIds,

    isPlaying: selectPlaylistIsPlaying(state, slug),
    isQueued: selectPlaylistIsQueued(state, slug),
  };
}

function getShareTrackingContext(props) {
  return {
    sharedContentType: 'Playlist',
    sharedContentId: props.playlist.id,
    contextType: 'Playlist',
    contentId: props.playlist.id,
  };
}

function getTrackingContext(props) {
  return {
    contextType: 'playlist',
    contextId: props.playlist.id.toString(),
  };
}

function getEntityTrackingData(props) {
  return {
    itemType: 'playlist',
    itemId: props.playlist.id,
  };
}

// Adds a proper displayName used for tracking
const Playlist = props => {
  const { userIsPatron } = props;
  return (
    <GenericTrackListView
      {...props}
      isOnlyRadioAvailable={__CAPACITOR__ && !userIsPatron}
      entityType="playlist"
    />
  );
};

Playlist.onDataFetch = (store, params, location, intl) => {
  const state = store.getState();
  const playlistSlug = params.slug;
  const playlist = selectPlaylist(state, playlistSlug);

  store.dispatch(
    clientActions.setHelmet({
      title: META_TITLE_PREFIX + playlist.title,
      description: intl.formatMessage(messages.playlistMetaDescription, {
        title: playlist.title,
      }),
      keywords: intl.formatMessage(messages.playlistMetaKeywords, {
        title: playlist.title,
      }),
      ogImage: imgixImageUrl({
        path: playlist.imageUrl,
        style: IMGIX_STYLE_4,
        width: PLAYABLE_ITEM_FULL_DIMS.w,
        height: PLAYABLE_ITEM_FULL_DIMS.h,
      }),
      googleSchema: buildPlaylistSchema(playlist, intl),
      canonicalRoute: `/playlists/${playlist.slug}`,
      ogType: 'music.playlist',
    })
  );

  if (!__CAPACITOR__ || !location.query || !location.query.trackId) {
    return;
  }
  // In capacitor we may have deeplinks that request a track to be played upon startup.
  const requestedTrack = location.query && location.query.trackId;

  store.dispatch(playerActions.rewind());

  const tracks = playlist.trackIds.map(playerActions.createItem);

  store.dispatch(
    playerActions.setQueueAndPlay(
      getQueueOrigin(ENTITY_TYPE_PLAYLIST, { slug: playlistSlug }),
      tracks,
      playerActions.getItemById(requestedTrack, tracks)
    )
  );
};

const trackingProps = (state, params) => {
  return {
    contextType: 'Playlist',
    contextId: selectPlaylistIdFromSlug(state, params.slug),
    contextSlug: params.slug,
  };
};

export default compose(
  trackedViewComponent('Playlist', trackingProps),
  dataComponent(fetchData),
  connect(mapStateToProps, {
    track: analyticsActions.track,
    showAddToPlaylistModal,
  }),
  shareableEntity(ENTITY_TYPE_PLAYLIST, getShareTrackingContext),
  collectibleEntity(ENTITY_TYPE_PLAYLIST, getCollectibleEntityDescription),
  playlistableEntity({
    getEntityTrackingData,
    getTrackingContext,
  }),
  queueOriginComponent(getPlaylistQueueOrigin, mapQueueOriginStateToProps, getTrackingContext)
)(Playlist);
